'use client';

import { buildCmsWorkspaceLaunchDetail } from '@/cms/components/blocks/workspace/workspace-launch-detail';
import { dispatchCmsWorkspaceLaunch } from '@/cms/components/blocks/workspace/workspace-session-controller';
import type { CmsWorkspaceLaunchContext } from '@/cms/workspace/launch-context';
import { useAuthSessionObserver } from '@/lib/auth/auth-session-observer';
import { useCallback, useEffect, useLayoutEffect } from 'react';

const useBrowserLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

export function useCmsWorkspaceAutoLaunch({
  canEnterWorkspaceMode,
  initialWorkspaceMode,
  isWorkspaceMode,
  suppressAuthenticatedAutoLaunch = false,
  workspaceContentAvailable,
  workspaceLaunchContext,
}: {
  canEnterWorkspaceMode: boolean;
  initialWorkspaceMode: boolean;
  isWorkspaceMode: boolean;
  suppressAuthenticatedAutoLaunch?: boolean;
  workspaceContentAvailable: boolean;
  workspaceLaunchContext?: CmsWorkspaceLaunchContext;
}) {
  const authSession = useAuthSessionObserver();
  const canLaunchWorkspace =
    !initialWorkspaceMode &&
    canEnterWorkspaceMode &&
    workspaceContentAvailable &&
    !isWorkspaceMode;
  const launchWorkspace = useCallback(() => {
    dispatchCmsWorkspaceLaunch(
      buildCmsWorkspaceLaunchDetail(workspaceLaunchContext, {
        forceAuthSessionRead: true,
      })
    );
  }, [workspaceLaunchContext]);

  useBrowserLayoutEffect(() => {
    if (
      suppressAuthenticatedAutoLaunch ||
      !canLaunchWorkspace ||
      authSession.status !== 'authenticated'
    ) {
      return;
    }

    launchWorkspace();
  }, [
    authSession.status,
    canLaunchWorkspace,
    launchWorkspace,
    suppressAuthenticatedAutoLaunch,
  ]);
}
